import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface StableDropdownOption {
  value: string;
  text: string;
  subtitle?: string;
  data?: any;
}

interface StableDropdownProps {
  options: StableDropdownOption[];
  value?: string;
  onSelect: (value: string, option: StableDropdownOption) => void;
  onSearch?: (query: string) => Promise<StableDropdownOption[]>;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  hideChevron?: boolean;
}

const StableDropdown: React.FC<StableDropdownProps> = ({
  options = [],
  value,
  onSelect,
  onSearch,
  placeholder = 'Select an option...',
  loading = false,
  disabled = false,
  className = '',
  hideChevron = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<StableDropdownOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const inputContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Find selected option
  const selectedOption = [...options, ...searchResults].find((opt) => opt.value === value);

  // Update dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (inputContainerRef.current) {
      const rect = inputContainerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  // Stable search function
  const handleSearch = useCallback(
    async (query: string) => {
      if (!onSearch || !query.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);

      try {
        const results = await onSearch(query);
        setSearchResults(results);

        // Force dropdown open after search
        if (results.length > 0) {
          setIsOpen(true);
          updateDropdownPosition();
        }
      } catch (error) {
        console.error('❌ StableDropdown: Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [onSearch, updateDropdownPosition]
  );

  // Debounced search
  const debouncedSearch = useCallback(
    (query: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(query);
      }, 300);
    },
    [handleSearch]
  );

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchTerm(query);

      if (query.trim()) {
        debouncedSearch(query);
        if (!isOpen) {
          setIsOpen(true);
          updateDropdownPosition();
        }
      } else {
        setSearchResults([]);
        setIsOpen(false);
      }
    },
    [debouncedSearch, isOpen, updateDropdownPosition]
  );

  // Handle input focus
  const handleInputFocus = useCallback(() => {
    updateDropdownPosition();
    if (searchTerm && searchResults.length > 0) {
      setIsOpen(true);
    } else if (searchTerm) {
      handleSearch(searchTerm);
    }
  }, [searchTerm, searchResults.length, updateDropdownPosition, handleSearch]);

  // Handle option select
  const handleOptionSelect = useCallback(
    (option: StableDropdownOption) => {
      onSelect(option.value, option);
      setSearchTerm(option.text); // Keep the selected text in the search term
      setSearchResults([]);
      setIsOpen(false);
    },
    [onSelect]
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking on input container or dropdown
      if (inputContainerRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }

      setIsOpen(false);
    };

    if (isOpen) {
      // Small delay to prevent immediate closure on focus
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();

      // Update position on scroll/resize
      const handlePositionUpdate = () => updateDropdownPosition();
      window.addEventListener('scroll', handlePositionUpdate);
      window.addEventListener('resize', handlePositionUpdate);

      return () => {
        window.removeEventListener('scroll', handlePositionUpdate);
        window.removeEventListener('resize', handlePositionUpdate);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Sync searchTerm with selected value
  useEffect(() => {
    if (selectedOption && !searchTerm) {
      setSearchTerm(selectedOption.text);
    } else if (!selectedOption && value && !searchTerm) {
      // Clear searchTerm if no matching option found
      setSearchTerm('');
    }
  }, [selectedOption, value, searchTerm]);

  // Display options (search results or default options)
  const displayOptions = searchTerm ? searchResults : options;

  // Dropdown portal content
  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="absolute bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
      style={{
        position: 'absolute',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
        minHeight: '50px',
      }}
      onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking dropdown
    >
      {displayOptions.length > 0 ? (
        displayOptions.map((option, index) => (
          <div
            key={option.value || index}
            className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
            onMouseDown={(e) => e.preventDefault()} // Prevent blur
            onClick={() => handleOptionSelect(option)}
          >
            <div className="font-medium text-gray-900">{option.text}</div>
            {option.subtitle && <div className="text-sm text-gray-500">{option.subtitle}</div>}
          </div>
        ))
      ) : searchTerm && !isSearching ? (
        <div className="px-4 py-3 text-gray-500 text-sm">No results found for "{searchTerm}"</div>
      ) : !searchTerm && options.length === 0 ? (
        <div className="px-4 py-3 text-gray-500 text-sm">Start typing to search...</div>
      ) : isSearching ? (
        <div className="px-4 py-3 text-gray-500 text-sm">Searching...</div>
      ) : null}
    </div>
  ) : null;

  return (
    <div ref={inputContainerRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        />
        {!hideChevron && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            {loading || isSearching ? (
              <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
            ) : (
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            )}
          </div>
        )}
        {hideChevron && (loading || isSearching) && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
          </div>
        )}
      </div>

      {/* Portal dropdown - rendered outside parent DOM tree */}
      {typeof window !== 'undefined' &&
        dropdownContent &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default StableDropdown;
